import { MouseEvent, ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react';
import { generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  PUBLIC_PRICING,
  PUBLIC_BLOG,
  PUBLIC_CONTACT_US,
  PUBLIC_TERMS,
  PUBLIC_SCOPE_AND_SEQUENCE,
  PUBLIC_TEACHER_BENEFITS,
  PUBLIC_SCHOOL_BENEFITS,
  PUBLIC_SUBJECT,
  PUBLIC_STIMULUS,
  PUBLIC_PRICING_FAQS,
  PUBLIC_ABOUT,
} from 'global/public-routes.constants';
import * as Styled from 'approot/shared/layout/footer/footer.style';
import FooterSocials from 'approot/shared/layout/footer/social/footer-socials';
import FooterLogo from 'approot/shared/layout/footer/footer-logo';
import { pushFooterNavSublinkDataEvent } from 'approot/shared/layout/navigation/navigation.utils';
import { Col, Row } from 'approot/shared/grid/grid';
import FooterRegions from 'approot/shared/layout/footer/regions/footer-regions';
import { useLayoutLinks } from 'approot/shared/layout/layout.hooks';
import {
  NAV_FOR_SCHOOLS_LABEL,
  NAV_LESSONS,
  NAV_PRODUCT,
  NAV_SCOPE_AND_SEQUENCE_LABEL,
  NAV_STIMULUS_LIBRARY_LABEL,
  NAV_FOR_TEACHERS_LABEL,
  NAV_PRICING_LABEL,
  NAV_TERMS_AND_PRIVACY_LABEL,
  NAV_SUPPORT,
  NAV_FAQS_LABEL,
  NAV_TEACHING_BLOG_LABEL,
  NAV_CONTACT_US_LABEL,
  NAV_LESSONS_LABEL,
  NAV_PRODUCT_LABEL,
  NAV_SUPPORT_LABEL,
  NAV_ABOUT_LABEL,
  NAV_DISCIPLINES_LABEL,
  NAV_SEND_MESSAGE_LABEL,
} from 'approot/shared/layout/navigation/mega/mega-nav.constants';
import { setContentFilterData } from 'approot/shared/filters/filters.apollo';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { NAMESPACE_SUPPORT } from 'internationalise';
import { INTERCOM_LAUNCH_TARGET_CLASS } from 'lib/intercom/intercom';
import { PRIVATE_ACCOUNT_REFER } from 'global/private-routes.constants';
import useUserData from 'approot/shared/user/user-data';
import { FlexLeftAlign } from 'approot/shared/styles/flex.style';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { FooterDisciplineLinks } from 'approot/shared/layout/footer/footer-discipline-links';
import { FooterSubjectLinks } from 'approot/shared/layout/footer/footer-subject-links';

export const FooterLink = ({
  slug,
  menu,
  label,
  onClick,
  ...rest
}: {
  slug: string;
  menu: string;
  label: string;
  onClick?: (e: MouseEvent) => void;
}) => (
  <li>
    <Styled.FooterLinkStyled
      to={slug}
      onClick={e => {
        onClick?.(e);
        pushFooterNavSublinkDataEvent(menu, label);
      }}
      {...rest}
    >
      {label}
    </Styled.FooterLinkStyled>
  </li>
);

export const FooterAnchor = ({
  href,
  menu,
  label,
  onClick,
  ...rest
}: {
  href: string;
  menu: string;
  label: string;
  onClick?: (e: MouseEvent) => void;
} & AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <li>
    <Styled.FooterAnchorStyled
      href={href}
      onClick={e => {
        onClick?.(e);
        pushFooterNavSublinkDataEvent(menu, label);
      }}
      {...rest}
    >
      {label}
    </Styled.FooterAnchorStyled>
  </li>
);

export const FooterButton = ({
  menu,
  label,
  onClick,
  ...rest
}: {
  menu: string;
  label: string;
  onClick?: (e: MouseEvent) => void;
} & ButtonHTMLAttributes<HTMLButtonElement>) => (
  <li>
    <Styled.FooterButtonStyled
      onClick={e => {
        onClick?.(e);
        pushFooterNavSublinkDataEvent(menu, label);
      }}
      {...rest}
    >
      {label}
    </Styled.FooterButtonStyled>
  </li>
);

export const FooterStimulusLinks = () => {
  const { goToScopeAndSequence } = useLayoutLinks();
  return (
    <>
      <FooterLink
        slug={PUBLIC_STIMULUS}
        menu={NAV_LESSONS}
        label={NAV_STIMULUS_LIBRARY_LABEL}
        data-selector="footer-stimulus-library"
      />
      <FooterLink
        slug={PUBLIC_SCOPE_AND_SEQUENCE}
        menu={NAV_LESSONS}
        label={NAV_SCOPE_AND_SEQUENCE_LABEL}
        onClick={goToScopeAndSequence}
        data-selector="footer-scope"
      />
    </>
  );
};

const Footer = () => {
  const { isSignedIn } = useUserData();
  const { isRegionUS } = useRegionData();
  const { data, loading, disciplines } = useGetStartupData();
  const { t } = useTranslation(NAMESPACE_SUPPORT);
  const subjects = data?.startupData.subjects;

  const { goToFaqs } = useLayoutLinks();

  return (
    <Styled.Footer data-selector="footer-section">
      <Styled.FooterWrapper>
        <Styled.FooterMainContainer>
          <Styled.FooterLogoContainer>
            <FooterLogo />
          </Styled.FooterLogoContainer>
          <Styled.FooterNavContainer>
            <Row>
              <Col
                md={6}
                data-selector={`group-${(isRegionUS
                  ? NAV_DISCIPLINES_LABEL
                  : NAV_LESSONS_LABEL
                ).toLowerCase()}`}
              >
                <Styled.FooterGroupHeading
                  aria-label={`${
                    isRegionUS ? NAV_DISCIPLINES_LABEL : NAV_LESSONS_LABEL
                  } Footer links`}
                >
                  {isRegionUS ? NAV_DISCIPLINES_LABEL : NAV_LESSONS_LABEL}
                </Styled.FooterGroupHeading>
                <Styled.FooterNavHR />
                {isRegionUS && (
                  <FooterDisciplineLinks
                    render={discipline => (
                      <FooterLink
                        key={discipline.name}
                        slug={discipline.slug}
                        menu={NAV_LESSONS}
                        label={discipline.label}
                        data-selector="footer-discipline-lnk"
                      />
                    )}
                    disciplines={disciplines}
                    loading={loading}
                    stimulusLinks={<FooterStimulusLinks />}
                  />
                )}
                {!isRegionUS && (
                  <FooterSubjectLinks
                    render={subject => (
                      <FooterLink
                        key={subject.name}
                        slug={generatePath(PUBLIC_SUBJECT, {
                          subject: subject.name,
                        })}
                        menu={NAV_LESSONS}
                        label={subject.label}
                        onClick={() => {
                          setContentFilterData({
                            triggerContentRefresh: true,
                          });
                        }}
                        data-selector={`footer-${subject.name}`}
                      />
                    )}
                    subjects={subjects}
                    loading={loading}
                    stimulusLinks={<FooterStimulusLinks />}
                  />
                )}
              </Col>

              <Col md={3} data-selector="group-product">
                <Styled.FooterGroupHeading
                  aria-label={`${NAV_PRODUCT_LABEL} Footer links`}
                >
                  {NAV_PRODUCT_LABEL}
                </Styled.FooterGroupHeading>
                <Styled.FooterNavHR />
                <Styled.FooterGroup>
                  <FooterLink
                    slug={PUBLIC_TEACHER_BENEFITS}
                    menu={NAV_PRODUCT}
                    label={NAV_FOR_TEACHERS_LABEL}
                    data-selector="footer-for-teachers"
                  />
                  <FooterLink
                    slug={PUBLIC_SCHOOL_BENEFITS}
                    menu={NAV_PRODUCT}
                    label={NAV_FOR_SCHOOLS_LABEL}
                    data-selector="footer-for-schools"
                  />
                  <FooterLink
                    slug={PUBLIC_PRICING}
                    menu={NAV_PRODUCT}
                    label={NAV_PRICING_LABEL}
                    data-selector="footer-pricing"
                  />
                  <FooterLink
                    slug={PUBLIC_ABOUT}
                    menu={NAV_PRODUCT}
                    label={NAV_ABOUT_LABEL}
                    data-selector="footer-about"
                  />
                  <FooterLink
                    slug={PUBLIC_TERMS}
                    menu={NAV_PRODUCT}
                    label={NAV_TERMS_AND_PRIVACY_LABEL}
                    data-selector="footer-terms"
                  />
                </Styled.FooterGroup>
              </Col>

              <Col md={3} data-selector="group-support">
                <Styled.FooterGroupHeading
                  aria-label={`${NAV_SUPPORT_LABEL} Footer links`}
                >
                  {NAV_SUPPORT_LABEL}
                </Styled.FooterGroupHeading>
                <Styled.FooterNavHR />
                <Styled.FooterGroup>
                  <FooterAnchor
                    href={t('helpCentreLink')}
                    target="_blank"
                    menu={NAV_SUPPORT}
                    label={t('helpCentreLinkLabel')}
                    data-selector="footer-help-centre"
                  />
                  <FooterButton
                    type="button"
                    className={INTERCOM_LAUNCH_TARGET_CLASS}
                    aria-haspopup="dialog"
                    menu={NAV_SUPPORT}
                    label={NAV_SEND_MESSAGE_LABEL}
                    data-selector="footer-send-message"
                  />
                  <FooterLink
                    slug={PUBLIC_PRICING_FAQS}
                    menu={NAV_SUPPORT}
                    label={NAV_FAQS_LABEL}
                    onClick={goToFaqs}
                    data-selector="footer-faqs"
                  />
                  <FooterLink
                    slug={PUBLIC_BLOG}
                    menu={NAV_SUPPORT}
                    label={NAV_TEACHING_BLOG_LABEL}
                    data-selector="footer-blog"
                  />
                  <FooterLink
                    slug={PUBLIC_CONTACT_US}
                    menu={NAV_SUPPORT}
                    label={NAV_CONTACT_US_LABEL}
                    data-selector="footer-contact"
                  />
                </Styled.FooterGroup>
              </Col>
            </Row>
          </Styled.FooterNavContainer>
        </Styled.FooterMainContainer>
        <Styled.FooterBreakStyled aria-hidden />
        <Styled.FooterSpaceBetween>
          <FlexLeftAlign>
            <FooterSocials />
            {isSignedIn && (
              <Styled.FooterReferLink to={PRIVATE_ACCOUNT_REFER}>
                Love Inquisitive? Refer and earn credits
              </Styled.FooterReferLink>
            )}
          </FlexLeftAlign>
          <FooterRegions />
        </Styled.FooterSpaceBetween>
        <Styled.FooterBreakStyled aria-hidden />
        <Styled.FooterTextStyled noBottomMargin>
          &copy; Inquisitive {t('company')}
        </Styled.FooterTextStyled>
      </Styled.FooterWrapper>
    </Styled.Footer>
  );
};

export default Footer;
